import React, {useState, useRef} from 'react';
import {useAuth} from '../../hooks/useAuth';
import {User, useAuthStore} from '../../stores/authStore';
import {useIsTouch} from '../../hooks/useIsTouch';

interface AccountWidgetProps {
  /** Opens the account modal, where an email address can be added, confirmed, or replaced. */
  onManageEmail: () => void;
}

function providerLabel(provider: string): string {
  switch (provider.toLowerCase()) {
    case 'google': return 'Google';
    case 'github': return 'GitHub';
    case 'discord': return 'Discord';
    default: return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

function ProviderIcon({provider}: {provider: string}) {
  switch (provider.toLowerCase()) {
    case 'google':
      // Simple coloured "G"
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{display: 'block'}}>
          <text x="8" y="12" textAnchor="middle" fontSize="13" fontWeight="bold"
            fill="#4285F4" fontFamily="Arial, sans-serif">G</text>
        </svg>
      );
    case 'github':
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="#ccc" style={{display: 'block'}}>
          <path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38
            0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52
            -.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2
            -3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82
            .64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08
            2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01
            1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/>
        </svg>
      );
    default:
      return <span style={{fontSize: 12, color: '#aaa'}}>{provider.charAt(0).toUpperCase()}</span>;
  }
}

function PendingEmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{display: 'block'}}>
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="#d8a657" strokeWidth="1.2"/>
      <path d="M1.5 5l6.5 4.5L14.5 5" stroke="#d8a657" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

function GuestIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="#888" style={{display: 'block'}}>
      <circle cx="8" cy="5.5" r="2.5"/>
      <path d="M2 13.5c0-3.31 2.69-6 6-6s6 2.69 6 6" stroke="#888" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{display: 'block'}}>
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="#888" strokeWidth="1.2"/>
      <path d="M1.5 5l6.5 4.5L14.5 5" stroke="#888" strokeWidth="1.2" strokeLinecap="round"/>
    </svg>
  );
}

const MAX_ADDRESS_CHARS = 20;

function truncateAddress(address: string): string {
  return address.length > MAX_ADDRESS_CHARS ? address.slice(0, MAX_ADDRESS_CHARS - 2) + '…' : address;
}

/**
 * An unconfirmed address takes priority over a confirmed one: a player mid-change needs the way
 * back to the pending state more than they need to start another change.
 */
function emailActionLabel(user: User): string {
  if (user.pendingEmail) return 'Confirm email';
  if (user.email) return 'Change email';
  return 'Add email';
}

export const AccountWidget: React.FC<AccountWidgetProps> = ({onManageEmail}) => {
  const {logout} = useAuth();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isTouch = useIsTouch();
  const [loggingOut, setLoggingOut] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [touchExpanded, setTouchExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const expanded = isTouch ? touchExpanded : hovered;

  const handleMouseEnter = () => {
    if (isTouch) return;
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setHovered(true);
  };

  const handleMouseLeave = () => {
    if (isTouch) return;
    collapseTimer.current = setTimeout(() => setHovered(false), 300);
  };

  if (!isAuthenticated || !user) return null;

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      if (isTouch) setTouchExpanded(false);
    }
  };

  // Touch: tapping icon toggles expand/collapse
  const handleIconClick = (e: React.MouseEvent) => {
    if (isTouch) {
      e.stopPropagation();
      setTouchExpanded(prev => !prev);
    }
  };

  // Touch: tapping anywhere else on the expanded widget fires logout
  const handleContainerClick = () => {
    if (isTouch && touchExpanded) void handleLogout();
  };

  // Prevent Log out button from also triggering the container click
  const handleLogoutClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void handleLogout();
  };

  // Same guard as logout — on touch, an unstopped click on the expanded widget logs the player out.
  const handleManageEmailClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTouch) setTouchExpanded(false);
    onManageEmail();
  };

  // Determine display label and icon
  const oauthAccount = user.oauthAccounts?.[0];
  let label: string;
  let icon: React.ReactNode;

  if (oauthAccount) {
    label = oauthAccount.providerUsername || providerLabel(oauthAccount.provider);
    icon = oauthAccount.providerAvatar
      ? <img src={oauthAccount.providerAvatar} alt="" width={20} height={20} referrerPolicy="no-referrer" style={{borderRadius: '50%', display: 'block'}}/>
      : <ProviderIcon provider={oauthAccount.provider}/>;
  } else if (user.email) {
    label = truncateAddress(user.email);
    icon = <EmailIcon/>;
  } else if (user.pendingEmail) {
    // Linked but unconfirmed: still a guest account until the emailed link is clicked.
    label = `${truncateAddress(user.pendingEmail)} — unconfirmed`;
    icon = <PendingEmailIcon/>;
  } else {
    label = 'Guest';
    icon = <GuestIcon/>;
  }

  const dividerStyle: React.CSSProperties = {
    width: expanded ? 1 : 0,
    height: 14,
    background: 'rgba(255,255,255,0.15)',
    flexShrink: 0,
    transition: 'width 0.2s ease-in-out',
  };

  // Shared by the two actions; each supplies its own colour and collapsed-to-zero max width.
  const actionStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    fontSize: 12,
    fontFamily: 'monospace',
    padding: 0,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    opacity: expanded ? 1 : 0,
    transition: 'max-width 0.2s ease-in-out, opacity 0.2s ease-in-out',
    flexShrink: 0,
  };

  return (
    <div
      onClick={handleContainerClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'fixed',
        top: 12,
        right: 12,
        zIndex: 6000,
        display: 'flex',
        alignItems: 'center',
        gap: expanded ? 8 : 0,
        background: 'rgba(10, 10, 20, 0.75)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20,
        padding: expanded ? '5px 10px 5px 8px' : '5px 8px',
        fontFamily: 'monospace',
        fontSize: 12,
        backdropFilter: 'blur(4px)',
        pointerEvents: 'auto',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        cursor: isTouch ? 'pointer' : (expanded ? 'default' : 'pointer'),
        userSelect: 'none',
      }}
    >
      <div
        onClick={handleIconClick}
        style={{display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0}}
      >
        {icon}
        <span style={{
          color: '#bbb',
          // Capped tighter than the identity alone would need: with two actions beside it the
          // expanded widget has to stay inside a narrow phone viewport, and it grows leftward
          // off-screen rather than clipping if it doesn't.
          maxWidth: expanded ? 140 : 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          opacity: expanded ? 1 : 0,
          transition: 'max-width 0.2s ease-in-out, opacity 0.2s ease-in-out',
        }}>{label}</span>
      </div>
      <div style={dividerStyle}/>
      <button
        onClick={handleManageEmailClick}
        style={{
          ...actionStyle,
          // Amber while unconfirmed, so the widget itself carries the reminder to finish the link.
          color: user.pendingEmail ? '#d8a657' : '#888',
          cursor: 'pointer',
          maxWidth: expanded ? 120 : 0,
        }}
      >
        {emailActionLabel(user)}
      </button>
      <div style={dividerStyle}/>
      <button
        onClick={handleLogoutClick}
        disabled={loggingOut}
        style={{
          ...actionStyle,
          color: loggingOut ? '#555' : '#888',
          cursor: loggingOut ? 'default' : 'pointer',
          maxWidth: expanded ? 100 : 0,
        }}
      >
        {loggingOut ? 'Logging out…' : 'Log out'}
      </button>
    </div>
  );
};
